// Attendance Feed & Roster — the screen that makes the OnTime feed accountable (mig 182).
//
// Why this exists: the feed failed four distinct ways in three weeks and every one was silent.
// The quarantine guard was working the whole time — 14 events logged since 24 July — but the only
// alert channel was an unset env var pointing at an SMTP relay that has been blocked since 19 July,
// so nothing was ever attempted. The 26–31 July outage self-resolved and nobody ever knew it had
// happened; the 5–9 August one ran for six days until a human happened to open the grid.
//
// Four panels, each answering a question that previously had no answer anywhere in the system:
//   • Health        — is the feed alive right now?
//   • Events        — what did it refuse, and WITH WHAT PAYLOAD (previously only counts survived)
//   • Backfill      — the bridge's 3-day window loses days permanently; ask for them back
//   • Reconciliation— who can never receive a punch, and which device codes nobody claims
import React, { useState, useEffect, useCallback } from 'react';
import api from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { HRC, Card, SectionTitle, Btn, Field, input, Empty, Loading } from './hrkit';
import ReportImport from './ReportImport';
import { reportError } from '../../utils/reportError';

const ADMIN_ROLES = ['admin', 'management'];
const fmtTs = (t) => (t ? new Date(t).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' }) : '—');

const KIND = {
  quarantine:  { label: 'Refused (all-zero roster)', color: '#B4690E', bg: '#FDF3E7' },
  stale:       { label: 'Feed silent',               color: '#A32020', bg: '#FDECEC' },
  error:       { label: 'Run failed',                color: '#A32020', bg: '#FDECEC' },
  zero_import: { label: 'Imported zero rows',        color: '#B4690E', bg: '#FDF3E7' },
  recovered:   { label: 'Recovered',                 color: '#1B7F4B', bg: '#E9F7EF' },
};

const th = { fontSize: 10, color: HRC.navy, fontWeight: 800, textTransform: 'uppercase',
  letterSpacing: 0.4, padding: '8px 10px', textAlign: 'left', background: HRC.violet,
  borderBottom: `1px solid ${HRC.border}`, whiteSpace: 'nowrap' };
const td = { fontSize: 12, color: HRC.text, padding: '7px 10px', borderBottom: '1px solid #F1F0EE',
  verticalAlign: 'top' };

// ── Panel 1 — health ─────────────────────────────────────────────────────────
function HealthPanel({ health, loading }) {
  if (loading) return <Card style={{ padding: 16 }}><Loading /></Card>;
  if (!health) return <Card style={{ padding: 16 }}><Empty>Feed health unavailable.</Empty></Card>;
  const stale = health.is_stale;
  const openQ = Number(health.open_quarantine?.n || 0);
  const stat = (label, value, tone) => (
    <div style={{ minWidth: 150 }}>
      <div style={{ fontSize: 10, color: HRC.muted, textTransform: 'uppercase', letterSpacing: 0.4, fontWeight: 800 }}>{label}</div>
      <div style={{ fontSize: 18, fontWeight: 800, color: tone || HRC.navy, marginTop: 3 }}>{value}</div>
    </div>
  );
  return (
    <Card style={{ padding: 16, marginBottom: 14,
      borderLeft: `4px solid ${stale ? '#A32020' : openQ ? '#B4690E' : '#1B7F4B'}` }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 26, alignItems: 'flex-start' }}>
        {stat('Status', stale ? '🔴 Silent' : openQ ? '⚠ Degraded' : '🟢 Live',
              stale ? '#A32020' : openQ ? '#B4690E' : '#1B7F4B')}
        {stat('Last successful import',
              health.hours_since_success == null ? 'never' : `${health.hours_since_success}h ago`,
              stale ? '#A32020' : undefined)}
        {stat('Last real punch', health.last_real_punch_date || '—')}
        {stat('Open refused dates', openQ || '0', openQ ? '#B4690E' : undefined)}
        {stat('Events (30d)', Object.values(health.events_30d || {}).reduce((a, b) => a + b, 0) || '0')}
      </div>
      <div style={{ marginTop: 12, fontSize: 11.5, color: HRC.muted, lineHeight: 1.6 }}>
        The bridge on the factory PC runs twice daily, so the widest healthy gap is the overnight
        one (~16h). Anything past <strong>{health.stale_threshold_hours}h</strong> means a run did not
        happen or did not reach the server, and raises an inbox alert to admin and management.
        {health.open_quarantine?.first && (
          <> Open refused dates span <strong>{health.open_quarantine.first}</strong> to <strong>{health.open_quarantine.last}</strong>.</>
        )}
      </div>
    </Card>
  );
}

// ── Panel 2 — events, with the payload that caused them ──────────────────────
function EventsPanel({ events, loading }) {
  const [open, setOpen] = useState(null);
  if (loading) return <Card style={{ padding: 16 }}><Loading /></Card>;
  return (
    <Card style={{ padding: 0, marginBottom: 14, overflow: 'hidden' }}>
      <div style={{ padding: '12px 16px', borderBottom: `1px solid ${HRC.border}` }}>
        <div style={{ fontSize: 13, fontWeight: 800, color: HRC.navy }}>Feed events</div>
        <div style={{ fontSize: 11, color: HRC.muted, marginTop: 2 }}>
          Every refusal, error and silence — with a sample of the payload that caused it. Before
          migration 182 only counts survived, so nobody could say which codes were failing.
        </div>
      </div>
      {!events.length ? <div style={{ padding: 16 }}><Empty>No feed events recorded.</Empty></div> : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr>
              <th style={th}>When</th><th style={th}>Kind</th><th style={th}>Company</th>
              <th style={th}>Date</th><th style={th}>Alerted</th><th style={th}>Detail</th><th style={th}></th>
            </tr></thead>
            <tbody>
              {events.map((e) => {
                const k = KIND[e.kind] || { label: e.kind, color: HRC.muted, bg: '#F4F4F6' };
                const hasPayload = (e.payload_sample && e.payload_sample.length)
                                || (e.unmatched_codes && e.unmatched_codes.length);
                return (
                  <React.Fragment key={e.id}>
                    <tr style={{ background: e.resolved_at ? '#FAFAFC' : undefined }}>
                      <td style={{ ...td, whiteSpace: 'nowrap', color: HRC.muted }}>{fmtTs(e.created_at)}</td>
                      <td style={td}>
                        <span style={{ background: k.bg, color: k.color, fontSize: 10.5, fontWeight: 800,
                          padding: '3px 7px', borderRadius: 5, whiteSpace: 'nowrap' }}>{k.label}</span>
                        {e.resolved_at && (
                          <span style={{ marginLeft: 6, fontSize: 10, color: '#1B7F4B', fontWeight: 700 }}>resolved</span>
                        )}
                      </td>
                      <td style={td}>{e.company || '—'}</td>
                      <td style={{ ...td, whiteSpace: 'nowrap', fontWeight: 700 }}>{e.work_date || '—'}</td>
                      {/* Did the alert actually get written? A 'no' here means the alert path itself
                          broke — the exact failure that hid all of this for three weeks. */}
                      <td style={td}>{e.notified
                        ? <span style={{ color: '#1B7F4B', fontWeight: 700 }}>✓</span>
                        : <span style={{ color: '#A32020', fontWeight: 700 }} title="No inbox notification was written for this event">✗</span>}</td>
                      <td style={{ ...td, maxWidth: 460, whiteSpace: 'pre-wrap', color: HRC.muted, fontSize: 11.5 }}>
                        {e.detail}
                      </td>
                      <td style={td}>
                        {hasPayload && (
                          <Btn variant="ghost" size="sm" onClick={() => setOpen(open === e.id ? null : e.id)}>
                            {open === e.id ? 'Hide' : 'Payload'}
                          </Btn>
                        )}
                      </td>
                    </tr>
                    {open === e.id && (
                      <tr>
                        <td colSpan={7} style={{ padding: 14, background: '#FAFAFC', borderBottom: '1px solid #F1F0EE' }}>
                          {!!(e.unmatched_codes || []).length && (
                            <div style={{ marginBottom: 12 }}>
                              <div style={{ fontSize: 11, fontWeight: 800, color: HRC.navy, marginBottom: 4 }}>
                                Device codes with no matching employee ({e.unmatched_codes.length})
                              </div>
                              <div style={{ fontSize: 12, fontFamily: 'ui-monospace,Menlo,monospace', color: HRC.text, lineHeight: 1.7 }}>
                                {e.unmatched_codes.join(', ')}
                              </div>
                            </div>
                          )}
                          {!!(e.payload_sample || []).length && (
                            <div>
                              <div style={{ fontSize: 11, fontWeight: 800, color: HRC.navy, marginBottom: 4 }}>
                                Payload sample ({e.payload_sample.length} record{e.payload_sample.length > 1 ? 's' : ''}
                                {e.records_received ? ` of ${e.records_received} received` : ''})
                              </div>
                              <pre style={{ margin: 0, fontSize: 11, lineHeight: 1.5, maxHeight: 260, overflow: 'auto',
                                background: '#fff', border: `1px solid ${HRC.border}`, borderRadius: 6, padding: 10 }}>
                                {JSON.stringify(e.payload_sample, null, 2)}
                              </pre>
                            </div>
                          )}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

// ── Panel 3 — backfill ───────────────────────────────────────────────────────
function BackfillPanel({ requests, loading, canEdit, onChange }) {
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');

  const submit = () => {
    setBusy(true); setMsg(''); setErr('');
    api.post('/hr/attendance/backfill-request', { from_date: from, to_date: to, reason: reason || undefined })
      .then(() => { setMsg(`Backfill queued for ${from} → ${to}.`); setFrom(''); setTo(''); setReason(''); onChange(); })
      .catch((e) => setErr(e.response?.data?.error || 'Could not queue the backfill'))
      .finally(() => setBusy(false));
  };
  const cancel = (id) => {
    api.patch(`/hr/attendance/backfill-request/${id}/cancel`).then(onChange).catch(reportError('cancel this backfill request'));
  };

  return (
    <Card style={{ padding: 16, marginBottom: 14 }}>
      <div style={{ fontSize: 13, fontWeight: 800, color: HRC.navy }}>Backfill a lost date range</div>
      <div style={{ fontSize: 11.5, color: HRC.muted, marginTop: 4, lineHeight: 1.6 }}>
        The bridge sends a fixed <strong>T-3 … T-1</strong> window, so a missed run drops those dates
        beyond reach — 5 and 6 August are already unrecoverable this way. Queueing a range here
        publishes it for the bridge to re-send.{' '}
        <strong style={{ color: '#8A5206' }}>
          This only completes once the factory-PC script is taught to poll for it — a DEEPAKPC task,
          not a change the server can make alone.
        </strong>{' '}
        Until then the row is a work order for a human, which still beats silent permanent loss.
      </div>

      {canEdit && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'flex-end', marginTop: 14 }}>
          <Field label="From date">
            <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} style={{ ...input, width: 160 }} />
          </Field>
          <Field label="To date">
            <input type="date" value={to} onChange={(e) => setTo(e.target.value)} style={{ ...input, width: 160 }} />
          </Field>
          <Field label="Reason (optional)">
            <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. bridge did not run"
              style={{ ...input, width: 260 }} />
          </Field>
          <Btn variant="primary" onClick={submit} disabled={busy || !from || !to}>
            {busy ? 'Queueing…' : 'Queue backfill'}
          </Btn>
        </div>
      )}
      {msg && <div style={{ marginTop: 10, fontSize: 12, color: '#1B7F4B' }}>{msg}</div>}
      {err && <div style={{ marginTop: 10, fontSize: 12, color: '#A32020' }}>{err}</div>}

      {loading ? <div style={{ marginTop: 14 }}><Loading /></div> : !requests.length ? null : (
        <div style={{ marginTop: 16, overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr>
              <th style={th}>Range</th><th style={th}>Status</th><th style={th}>Requested</th>
              <th style={th}>By</th><th style={th}>Reason</th><th style={th}>Rows</th><th style={th}></th>
            </tr></thead>
            <tbody>
              {requests.map((b) => (
                <tr key={b.id}>
                  <td style={{ ...td, fontWeight: 700, whiteSpace: 'nowrap' }}>{b.from_date} → {b.to_date}</td>
                  <td style={td}>
                    <span style={{ fontSize: 10.5, fontWeight: 800, padding: '3px 7px', borderRadius: 5,
                      background: b.status === 'fulfilled' ? '#E9F7EF' : b.status === 'cancelled' ? '#F4F4F6' : '#FDF3E7',
                      color: b.status === 'fulfilled' ? '#1B7F4B' : b.status === 'cancelled' ? HRC.muted : '#8A5206' }}>
                      {b.status}
                    </span>
                  </td>
                  <td style={{ ...td, color: HRC.muted, whiteSpace: 'nowrap' }}>{fmtTs(b.created_at)}</td>
                  <td style={td}>{b.requested_by_name || '—'}</td>
                  <td style={{ ...td, color: HRC.muted }}>{b.reason || '—'}</td>
                  <td style={{ ...td, textAlign: 'center' }}>{b.rows_delivered || '·'}</td>
                  <td style={td}>
                    {canEdit && ['pending', 'claimed'].includes(b.status) && (
                      <Btn variant="ghost" size="sm" onClick={() => cancel(b.id)}>Cancel</Btn>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

// ── Panel 4 — roster reconciliation ──────────────────────────────────────────
function ReconPanel({ recon, loading, days, setDays }) {
  if (loading) return <Card style={{ padding: 16 }}><Loading /></Card>;
  if (!recon) return null;
  const t = recon.totals || {};
  const missing = recon.missing_on_device || [];
  const unknown = recon.unknown_codes || [];
  return (
    <Card style={{ padding: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12 }}>
        <div>
          <div style={{ fontSize: 13, fontWeight: 800, color: HRC.navy }}>Roster reconciliation</div>
          <div style={{ fontSize: 11.5, color: HRC.muted, marginTop: 4, lineHeight: 1.6, maxWidth: 720 }}>
            <strong>{t.active_employees}</strong> active employees, <strong>{t.with_ontime_id}</strong> carrying an
            OnTime code, but only <strong>{t.seen_last_14d}</strong> device codes have sent a punch in the last
            14 days. Everyone below is enrolled in the ERP but is not reaching it from the device —
            a broken enrolment and an unrecorded exit look identical here, so treat this as a
            worklist, not a verdict.
          </div>
        </div>
        <Field label="Not seen for">
          <select value={days} onChange={(e) => setDays(Number(e.target.value))}
            style={{ ...input, width: 'auto', minWidth: 130, padding: '7px 10px' }}>
            {[7, 14, 30, 60, 90].map((d) => <option key={d} value={d}>{d}+ days</option>)}
          </select>
        </Field>
      </div>

      <div style={{ marginTop: 16, fontSize: 12, fontWeight: 800, color: HRC.navy }}>
        ERP employees with no recent device presence ({missing.length})
      </div>
      {!missing.length ? <div style={{ marginTop: 8 }}><Empty>Every active employee has punched recently.</Empty></div> : (
        <div style={{ marginTop: 8, overflowX: 'auto', maxHeight: 420, overflowY: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr>
              <th style={{ ...th, position: 'sticky', top: 0, zIndex: 1 }}>Code</th>
              <th style={{ ...th, position: 'sticky', top: 0, zIndex: 1 }}>Name</th>
              <th style={{ ...th, position: 'sticky', top: 0, zIndex: 1 }}>OnTime ID</th>
              <th style={{ ...th, position: 'sticky', top: 0, zIndex: 1 }}>Dept</th>
              <th style={{ ...th, position: 'sticky', top: 0, zIndex: 1 }}>Company</th>
              <th style={{ ...th, position: 'sticky', top: 0, zIndex: 1 }}>Last punch</th>
            </tr></thead>
            <tbody>
              {missing.map((m) => (
                <tr key={m.id}>
                  <td style={{ ...td, fontWeight: 700, color: HRC.navy }}>{m.employee_code}</td>
                  <td style={td}>{m.full_name}</td>
                  <td style={{ ...td, fontFamily: 'ui-monospace,Menlo,monospace' }}>
                    {m.ontime_emp_id || <span style={{ color: '#A32020', fontWeight: 700 }}>not mapped</span>}
                  </td>
                  <td style={{ ...td, color: HRC.muted }}>{m.department_name || '—'}</td>
                  <td style={{ ...td, color: HRC.muted }}>{m.company || '—'}</td>
                  <td style={td}>
                    {m.never_seen || !m.last_work_date
                      ? <span style={{ color: '#A32020', fontWeight: 700 }}>never</span>
                      : <span>{m.last_work_date} <span style={{ color: HRC.muted }}>({m.days_since}d)</span></span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div style={{ marginTop: 20, fontSize: 12, fontWeight: 800, color: HRC.navy }}>
        Device codes with no ERP employee ({unknown.length})
      </div>
      <div style={{ fontSize: 11, color: HRC.muted, marginTop: 3 }}>
        The device sends these; nobody in the ERP claims them. Each one is a punch being thrown
        away every single day. This list fills in as syncs land — codes rejected before migration
        182 were discarded and cannot be recovered.
      </div>
      {!unknown.length ? <div style={{ marginTop: 8 }}><Empty>No unclaimed device codes recorded yet.</Empty></div> : (
        <div style={{ marginTop: 8, overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr>
              <th style={th}>Device code</th><th style={th}>First seen</th><th style={th}>Last seen</th>
              <th style={th}>Last work date</th><th style={th}>Times</th><th style={th}>Last in / out</th>
            </tr></thead>
            <tbody>
              {unknown.map((u) => (
                <tr key={u.ontime_code}>
                  <td style={{ ...td, fontWeight: 700, fontFamily: 'ui-monospace,Menlo,monospace', color: HRC.navy }}>{u.ontime_code}</td>
                  <td style={{ ...td, color: HRC.muted, whiteSpace: 'nowrap' }}>{fmtTs(u.first_seen_at)}</td>
                  <td style={{ ...td, color: HRC.muted, whiteSpace: 'nowrap' }}>{fmtTs(u.last_seen_at)}</td>
                  <td style={td}>{u.last_work_date || '—'}</td>
                  <td style={{ ...td, textAlign: 'center' }}>{u.times_seen}</td>
                  <td style={{ ...td, fontFamily: 'ui-monospace,Menlo,monospace' }}>
                    {(u.last_in_time || '—')} / {(u.last_out_time || '—')}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

// Punches the feed could not place (mig 397). Previously discarded; now queued, one row per code and day.
// Nothing is guessed or auto-linked: a named person claims a code for an employee, and from then on it matches.
function UnmatchedPanel({ data, loading, onChange }) {
  const [emps, setEmps] = useState([]);
  const [form, setForm] = useState({ code: '', employee_id: '', basis: '' });
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');
  useEffect(() => {
    if (!data?.can_claim) return;
    api.get('/hr/employees', { params: { status: 'active' } }).then((r) => setEmps(r.data?.data || [])).catch(() => setEmps([]));
  }, [data?.can_claim]);
  const claim = () => {
    setBusy(true); setMsg(''); setErr('');
    api.post('/hr/attendance/device-claims', { ontime_code: form.code, employee_id: form.employee_id, basis: form.basis })
      .then((r) => { setMsg(`${form.code} claimed for ${r.data?.data?.employee_code}. ${r.data?.data?.note || ''}`); setForm({ code: '', employee_id: '', basis: '' }); onChange(); })
      .catch((e) => setErr(e.response?.data?.error || 'Could not record the claim'))
      .finally(() => setBusy(false));
  };
  if (loading) return <Card style={{ padding: 16, marginBottom: 14 }}><Loading /></Card>;
  const queue = data?.queue || [];
  const summary = data?.summary_only || [];
  const th = { textAlign: 'left', padding: '6px 8px', fontSize: 11, color: HRC.muted, borderBottom: `1px solid ${HRC.line || '#e5e7eb'}` };
  const td = { padding: '6px 8px', fontSize: 12, borderBottom: `1px solid ${HRC.line || '#f1f1f1'}` };
  return (
    <Card style={{ padding: 16, marginBottom: 14 }}>
      <div style={{ fontSize: 13, fontWeight: 800, color: HRC.navy }}>Punches the feed could not place ({queue.length} codes)</div>
      <div style={{ fontSize: 11.5, color: HRC.muted, marginTop: 4, lineHeight: 1.6 }}>
        A code that matches no employee, or only an <strong>inactive</strong> one, used to be discarded. It is now kept here, one row per day.
        Nothing is guessed and nothing is linked automatically. <strong>{data?.claim_rule}</strong>
        {' '}A claim makes <strong>future</strong> punches match; punches already queued stay here (turning them into attendance moves wages, so it is a separate decision).
      </div>
      {queue.length === 0 ? <Empty>No unplaced punches queued yet.</Empty> : (
        <div style={{ overflowX: 'auto', marginTop: 10 }}>
          <table style={{ borderCollapse: 'collapse', width: '100%' }}>
            <thead><tr><th style={th}>Code</th><th style={th}>Why</th><th style={th}>Days (with a punch)</th><th style={th}>From – to</th><th style={th}>Typical day</th><th style={th}>Last day</th><th style={th}>Claim</th></tr></thead>
            <tbody>{queue.map((q) => (
              <tr key={q.ontime_code}>
                <td style={{ ...td, fontFamily: 'monospace' }}>{q.ontime_code}</td>
                <td style={td}>{q.reason === 'inactive_employee' ? <>held by <strong>inactive</strong> {q.inactive_code} {q.inactive_name}</> : 'matches no employee'}</td>
                <td style={td}>{q.days_listed} ({q.days_with_punch})</td>
                <td style={td}>{q.first_date} – {q.last_date}</td>
                <td style={td}>{q.typical_in ? `${q.typical_in}–${q.typical_out || '?'}` : 'no punches'}</td>
                <td style={td}>{q.last_in || '—'}–{q.last_out || '—'}</td>
                <td style={td}>{q.claimed_code ? <>→ {q.claimed_code} {q.claimed_name} <span style={{ color: HRC.muted }}>by {q.claimed_by}</span></> : <span style={{ color: HRC.muted }}>unclaimed</span>}</td>
              </tr>))}</tbody>
          </table>
        </div>)}
      {summary.length > 0 && (
        <div style={{ fontSize: 11.5, color: HRC.muted, marginTop: 10 }}>
          <strong>Known only from before this queue existed ({summary.length}):</strong> the feed kept a count and the last day only; every other day was discarded.{' '}
          {summary.map((r) => `${r.ontime_code} (seen ${r.times_seen}×, last ${r.last_date} ${r.last_in || ''}–${r.last_out || ''})`).join(' · ')}
        </div>)}
      {data?.can_claim ? (
        <div style={{ display: 'grid', gridTemplateColumns: '140px 1fr 2fr auto', gap: 8, marginTop: 12, alignItems: 'end' }}>
          <Field label="Code"><select style={input} value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })}>
            <option value="">— code —</option>{[...queue.filter((q) => !q.claimed_code).map((q) => q.ontime_code), ...summary.map((r) => r.ontime_code)].map((c) => <option key={c} value={c}>{c}</option>)}
          </select></Field>
          <Field label="Employee (code · name)"><select style={input} value={form.employee_id} onChange={(e) => setForm({ ...form, employee_id: e.target.value })}>
            <option value="">— pick the person —</option>{emps.map((e) => <option key={e.id} value={e.id}>{e.employee_code} · {e.full_name}</option>)}
          </select></Field>
          <Field label="How you know it is this person (20+ characters)"><input style={input} value={form.basis} onChange={(e) => setForm({ ...form, basis: e.target.value })} /></Field>
          <Btn onClick={claim} disabled={busy || !form.code || !form.employee_id}>{busy ? 'Saving…' : 'Claim'}</Btn>
        </div>
      ) : (
        <div style={{ fontSize: 11.5, color: '#8A5206', marginTop: 10 }}>
          Claiming is locked for this login: a code is claimed by a named person on a <strong>personal</strong> HR, admin or management login. A shared login cannot claim an identity.
        </div>)}
      {msg && <div style={{ color: HRC.green || '#15803d', fontSize: 12, marginTop: 8 }}>{msg}</div>}
      {err && <div style={{ color: HRC.red, fontSize: 12, marginTop: 8 }}>{err}</div>}
    </Card>
  );
}

export default function FeedHealth() {
  const { user } = useAuth();
  const canEdit = ADMIN_ROLES.includes(user?.role);
  const [health, setHealth] = useState(null);
  const [events, setEvents] = useState([]);
  const [requests, setRequests] = useState([]);
  const [recon, setRecon] = useState(null);
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(true);
  const [reconLoading, setReconLoading] = useState(true);
  const [unmatched, setUnmatched] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      api.get('/hr/attendance/feed-health').then((r) => setHealth(r.data?.data || null)).catch(() => setHealth(null)),
      api.get('/hr/attendance/feed-events', { params: { limit: 60 } }).then((r) => setEvents(r.data?.data || [])).catch(() => setEvents([])),
      api.get('/hr/attendance/backfill-request').then((r) => setRequests(r.data?.data || [])).catch(() => setRequests([])),
      api.get('/hr/attendance/unmatched-punches').then((r) => setUnmatched(r.data?.data || null)).catch(() => setUnmatched(null)),
    ]).finally(() => setLoading(false));
  }, []);

  useEffect(() => load(), [load]);

  useEffect(() => {
    let alive = true;
    setReconLoading(true);
    api.get('/hr/attendance/reconciliation', { params: { days } })
      .then((r) => { if (alive) setRecon(r.data?.data || null); })
      .catch(() => { if (alive) setRecon(null); })
      .finally(() => { if (alive) setReconLoading(false); });
    return () => { alive = false; };
  }, [days]);

  return (
    <div>
      <SectionTitle right={<Btn variant="ghost" size="sm" onClick={load}>⟲ Refresh</Btn>}>
        Attendance Feed &amp; Roster
      </SectionTitle>
      <HealthPanel health={health} loading={loading} />
      {/* Placed directly under health: when the feed is silent this is the thing to reach for. */}
      {canEdit && <ReportImport onImported={load} />}
      <EventsPanel events={events} loading={loading} />
      <UnmatchedPanel data={unmatched} loading={loading} onChange={load} />
      <BackfillPanel requests={requests} loading={loading} canEdit={canEdit} onChange={load} />
      <ReconPanel recon={recon} loading={reconLoading} days={days} setDays={setDays} />
    </div>
  );
}
